import React, { useState, useEffect } from "react";
import axios from "axios";
import { FolderPlus, Trash2, Edit2, Save, X } from "lucide-react";


const ClientManagement = ({ currentUser, onClientUpdate }) => {
    const [clients, setClients] = useState([]);
    const [formData, setFormData] = useState({
        name: "",
        normal_qc: "2.0",
        priority_qc: "2.0",
        normal_revised: "1.0",
        priority_revised: "1.0"
    });

    const [editingClientId, setEditingClientId] = useState(null);
    const [editFormData, setEditFormData] = useState({
        name: "",
        normal_qc: "",
        priority_qc: "",
        normal_revised: "",
        priority_revised: ""
    });

    const fetchClients = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get("/api/clients", {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data && Array.isArray(res.data)) {
                setClients(res.data);
            }
        } catch (err) {
            console.error("Failed to fetch clients", err);
        }
    };

    useEffect(() => {
        fetchClients();
    }, []);

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleCreateClient = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) return;

        try {
            const token = localStorage.getItem("token");
            const res = await axios.post("/api/clients", {
                name: formData.name.trim(),
                normal_qc: parseFloat(formData.normal_qc),
                priority_qc: parseFloat(formData.priority_qc),
                normal_revised: parseFloat(formData.normal_revised),
                priority_revised: parseFloat(formData.priority_revised)
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data?.success) {
                alert("Client added successfully!");
                setFormData({
                    name: "",
                    normal_qc: "2.0",
                    priority_qc: "2.0",
                    normal_revised: "1.0",
                    priority_revised: "1.0"
                });
                fetchClients();
                if (typeof onClientUpdate === "function") {
                    onClientUpdate();
                }
            }
        } catch (err) {
            alert(err.response?.data?.message || "Failed to add client. Check connection.");
        }
    };

    const handleStartEdit = (client) => {
        setEditingClientId(client.id);
        setEditFormData({
            name: client.name,
            normal_qc: String(client.normal_qc),
            priority_qc: String(client.priority_qc),
            normal_revised: String(client.normal_revised),
            priority_revised: String(client.priority_revised)
        });
    };

    const handleCancelEdit = () => {
        setEditingClientId(null);
    };

    const handleEditInputChange = (e) => {
        setEditFormData({ ...editFormData, [e.target.name]: e.target.value });
    };

    const handleUpdateClient = async (id) => {
        if (!editFormData.name.trim()) return;

        try {
            const token = localStorage.getItem("token");
            const res = await axios.put(`/api/clients/${id}`, {
                name: editFormData.name.trim(),
                normal_qc: parseFloat(editFormData.normal_qc),
                priority_qc: parseFloat(editFormData.priority_qc),
                normal_revised: parseFloat(editFormData.normal_revised),
                priority_revised: parseFloat(editFormData.priority_revised)
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data?.success) {
                alert("Client updated successfully!");
                setEditingClientId(null);
                fetchClients();
                if (typeof onClientUpdate === "function") {
                    onClientUpdate();
                }
            }
        } catch (err) {
            alert(err.response?.data?.message || "Failed to update client.");
        }
    };

    const handleDeleteClient = async (id, clientName) => {
        if (!id) return;
        const confirmMsg = `Are you sure you want to delete client '${clientName}'?\nAll files belonging to this client will remain in database but the custom TAT targets will default back to global fallback settings.`;
        if (!window.confirm(confirmMsg)) return;

        try {
            const token = localStorage.getItem("token");
            await axios.delete(`/api/clients/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert("Client deleted successfully!");
            fetchClients();
            if (typeof onClientUpdate === "function") {
                onClientUpdate();
            }
        } catch (err) {
            alert(err.response?.data?.message || "Failed to delete client.");
        }
    };

    return (
        <div style={{ padding: "24px", backgroundColor: "#ffffff", borderRadius: "12px", boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)", border: "1px solid #f1f5f9" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px", borderBottom: "1px solid #e2e8f0", paddingBottom: "16px" }}>
                <h3 style={{ margin: 0, color: "#0f172a", fontWeight: "700", fontSize: "18px", display: "flex", alignItems: "center", gap: "8px" }}>
                    💼 Client TAT Target Settings
                </h3>
                <span style={{ fontSize: "12px", color: "#64748b", backgroundColor: "#f1f5f9", padding: "6px 12px", borderRadius: "20px", fontWeight: "600" }}>
                    {clients.length} Active Clients
                </span>
            </div>

            {/* Client Creation Form */}
            <form onSubmit={handleCreateClient} style={{ backgroundColor: "#f8fafc", padding: "20px", borderRadius: "10px", marginBottom: "28px", border: "1px solid #e2e8f0" }}>
                <h4 style={{ margin: "0 0 16px 0", color: "#334155", fontSize: "14px", fontWeight: "600" }}>➕ Register New Client</h4>
                
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px", marginBottom: "20px" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>Client Name</label>
                        <input
                            type="text"
                            name="name"
                            placeholder="e.g. GOSOURCE"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px", outline: "none", transition: "border-color 0.2s" }}
                        />
                    </div>
                    
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>QC Normal TAT (Hours)</label>
                        <input
                            type="number"
                            name="normal_qc"
                            step="0.1"
                            min="0"
                            value={formData.normal_qc}
                            onChange={handleInputChange}
                            required
                            style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px", outline: "none" }}
                        />
                    </div>
                    
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>QC Priority TAT (Hours)</label>
                        <input
                            type="number"
                            name="priority_qc"
                            step="0.1"
                            min="0"
                            value={formData.priority_qc}
                            onChange={handleInputChange}
                            required
                            style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px", outline: "none" }}
                        />
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>Revised Normal TAT (Hours)</label>
                        <input
                            type="number"
                            name="normal_revised"
                            step="0.1"
                            min="0"
                            value={formData.normal_revised}
                            onChange={handleInputChange}
                            required
                            style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px", outline: "none" }}
                        />
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>Revised Priority TAT (Hours)</label>
                        <input
                            type="number"
                            name="priority_revised"
                            step="0.1"
                            min="0"
                            value={formData.priority_revised}
                            onChange={handleInputChange}
                            required
                            style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px", outline: "none" }}
                        />
                    </div>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button type="submit" style={{ padding: "10px 20px", backgroundColor: "#00a3e0", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: "600", transition: "background-color 0.2s", boxShadow: "0 2px 4px rgba(0,163,224,0.2)" }}>
                        <FolderPlus size={16} /> Add Client & SLA Rule
                    </button>
                </div>
            </form>

            {/* Clients Table */}
            <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: "10px", boxShadow: "0 2px 8px rgba(0,0,0,0.02)" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", textAlign: "left" }}>
                    <thead>
                        <tr style={{ backgroundColor: "#f8fafc", borderBottom: "2px solid #e2e8f0", color: "#475569" }}>
                            <th style={{ padding: "14px 18px", fontWeight: "600" }}>Client Name</th>
                            <th style={{ padding: "14px 18px", fontWeight: "600" }}>QC Normal Target</th>
                            <th style={{ padding: "14px 18px", fontWeight: "600" }}>QC Priority Target</th>
                            <th style={{ padding: "14px 18px", fontWeight: "600" }}>Revised Normal Target</th>
                            <th style={{ padding: "14px 18px", fontWeight: "600" }}>Revised Priority Target</th>
                            <th style={{ padding: "14px 18px", fontWeight: "600", textAlign: "center" }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {clients.length === 0 ? (
                            <tr>
                                <td colSpan="6" style={{ padding: "30px", textAlign: "center", color: "#64748b", fontStyle: "italic", backgroundColor: "#ffffff" }}>
                                    No clients found.
                                </td>
                            </tr>
                        ) : (
                            clients.map((client, idx) => {
                                const rowBg = idx % 2 === 0 ? "#ffffff" : "#f8fafc";
                                const isEditing = client.id === editingClientId;

                                return (
                                    <tr key={client.id} style={{ borderBottom: "1px solid #e2e8f0", backgroundColor: rowBg, transition: "background-color 0.15s" }}>
                                        {isEditing ? (
                                            <>
                                                <td style={{ padding: "10px 14px" }}>
                                                    <input
                                                        type="text"
                                                        name="name"
                                                        value={editFormData.name}
                                                        onChange={handleEditInputChange}
                                                        style={{ padding: "6px 10px", borderRadius: "4px", border: "1px solid #cbd5e1", width: "100%", boxSizing: "border-box" }}
                                                    />
                                                </td>
                                                <td style={{ padding: "10px 14px" }}>
                                                    <input
                                                        type="number"
                                                        name="normal_qc"
                                                        step="0.1"
                                                        min="0"
                                                        value={editFormData.normal_qc}
                                                        onChange={handleEditInputChange}
                                                        style={{ padding: "6px 10px", borderRadius: "4px", border: "1px solid #cbd5e1", width: "100px" }}
                                                    />
                                                </td>
                                                <td style={{ padding: "10px 14px" }}>
                                                    <input
                                                        type="number"
                                                        name="priority_qc"
                                                        step="0.1"
                                                        min="0"
                                                        value={editFormData.priority_qc}
                                                        onChange={handleEditInputChange}
                                                        style={{ padding: "6px 10px", borderRadius: "4px", border: "1px solid #cbd5e1", width: "100px" }}
                                                    />
                                                </td>
                                                <td style={{ padding: "10px 14px" }}>
                                                    <input
                                                        type="number"
                                                        name="normal_revised"
                                                        step="0.1"
                                                        min="0"
                                                        value={editFormData.normal_revised}
                                                        onChange={handleEditInputChange}
                                                        style={{ padding: "6px 10px", borderRadius: "4px", border: "1px solid #cbd5e1", width: "100px" }}
                                                    />
                                                </td>
                                                <td style={{ padding: "10px 14px" }}>
                                                    <input
                                                        type="number"
                                                        name="priority_revised"
                                                        step="0.1"
                                                        min="0"
                                                        value={editFormData.priority_revised}
                                                        onChange={handleEditInputChange}
                                                        style={{ padding: "6px 10px", borderRadius: "4px", border: "1px solid #cbd5e1", width: "100px" }}
                                                    />
                                                </td>
                                                <td style={{ padding: "10px 14px", textAlign: "center" }}>
                                                    <div style={{ display: "flex", gap: "8px", justifyContent: "center", alignItems: "center" }}>
                                                        <button
                                                            onClick={() => handleUpdateClient(client.id)}
                                                            style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", display: "inline-flex" }}
                                                            title="Save changes"
                                                        >
                                                            <Save size={16} color="#10b981" />
                                                        </button>
                                                        <button
                                                            onClick={handleCancelEdit}
                                                            style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", display: "inline-flex" }}
                                                            title="Cancel"
                                                        >
                                                            <X size={16} color="#64748b" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td style={{ padding: "14px 18px", fontWeight: "600", color: "#1e293b" }}>{client.name}</td>
                                                <td style={{ padding: "14px 18px", color: "#334155" }}>{client.normal_qc} hours</td>
                                                <td style={{ padding: "14px 18px", color: "#334155" }}>{client.priority_qc} hours</td>
                                                <td style={{ padding: "14px 18px", color: "#334155" }}>{client.normal_revised} hours</td>
                                                <td style={{ padding: "14px 18px", color: "#334155" }}>{client.priority_revised} hours</td>
                                                <td style={{ padding: "14px 18px", textAlign: "center" }}>
                                                    <div style={{ display: "flex", gap: "8px", justifyContent: "center", alignItems: "center" }}>
                                                        <button
                                                            onClick={() => handleStartEdit(client)}
                                                            style={{
                                                                background: "none",
                                                                border: "none",
                                                                cursor: "pointer",
                                                                padding: "4px",
                                                                borderRadius: "4px",
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                transition: "background-color 0.2s"
                                                            }}
                                                            title="Edit client TAT targets"
                                                        >
                                                            <Edit2 size={16} color="#00a3e0" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteClient(client.id, client.name)}
                                                            style={{
                                                                background: "none",
                                                                border: "none",
                                                                cursor: "pointer",
                                                                padding: "4px",
                                                                borderRadius: "4px",
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                transition: "background-color 0.2s"
                                                            }}
                                                            title="Delete client"
                                                        >
                                                            <Trash2 size={16} color="#ef4444" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ClientManagement;
